import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Filter, Tag, Truck, Weight, X } from 'lucide-react';
import api from '../api';
import { useMasterData } from '../../utils/useMasterData';

const S = `
  .dc-wrap { font-family: 'Inter', sans-serif; color: #111827; }
  .dc-top { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; margin-bottom:14px; }
  .dc-title { margin:0; font-size:22px; font-weight:800; color:#111827; }
  .dc-sub { margin-top:4px; color:#1a1a1a; font-size:13px; }
  .dc-layout { display:grid; grid-template-columns: 280px 1fr; gap:14px; }
  .dc-filter-card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; box-shadow:0 2px 10px rgba(0,0,0,.05); padding:14px; height:max-content; }
  .dc-filter-title { font-size:13px; font-weight:800; color:#111827; margin-bottom:10px; display:flex; align-items:center; gap:8px; }
  .dc-input { width:100%; box-sizing:border-box; border:1px solid #d1d5db; border-radius:10px; padding:10px 12px; font-size:13px; color:#111827; background:#fff; }
  .dc-label { font-size:12px; color:#1a1a1a; font-weight:700; margin-bottom:6px; display:block; }
  .dc-cat-list { display:grid; gap:6px; max-height:220px; overflow:auto; }
  .dc-cat-item { display:flex; align-items:center; gap:8px; font-size:13px; color:#000; }
  .dc-grid { display:grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap:14px; }
  .dc-card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; box-shadow:0 2px 10px rgba(0,0,0,.05); overflow:hidden; cursor:pointer; }
  .dc-img { width:100%; height:190px; object-fit:cover; background:#f3f4f6; }
  .dc-body { padding:12px; }
  .dc-name { font-size:14px; font-weight:800; color:#111827; margin-bottom:6px; }
  .dc-chip { display:inline-flex; align-items:center; gap:4px; background:#f3f4f6; color:#000; font-size:11px; font-weight:700; border-radius:999px; padding:3px 10px; margin-bottom:8px; }
  .dc-row { display:flex; justify-content:space-between; font-size:12px; color:#1a1a1a; margin-bottom:4px; }
  .dc-price { font-size:15px; font-weight:900; color:#111827; }
  .dc-empty { text-align:center; padding:44px 10px; color:#1a1a1a; background:#fff; border:1px dashed #d1d5db; border-radius:14px; }
  .dc-mobile-toggle { display:none; margin-bottom:10px; }
  .dc-btn { border:1px solid #d1d5db; border-radius:9px; background:#fff; color:#111827; padding:8px 12px; font-size:12px; font-weight:700; cursor:pointer; min-height:38px; }

  .dc-skeleton { display:grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap:14px; }
  .dc-sk { height:280px; border-radius:14px; background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%); background-size:200% 100%; animation: shimmer 1.5s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

  .dc-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.5); z-index:1200; display:flex; align-items:center; justify-content:center; padding:16px; }
  .dc-modal { width:100%; max-width:760px; background:#fff; border-radius:16px; box-shadow:0 16px 36px rgba(0,0,0,.25); overflow:hidden; }
  .dc-modal-head { padding:14px 16px; border-bottom:1px solid #e5e7eb; display:flex; justify-content:space-between; align-items:center; }
  .dc-modal-body { padding:16px; display:grid; grid-template-columns: 280px 1fr; gap:14px; }
  .dc-modal-img { width:100%; height:280px; object-fit:cover; border-radius:12px; background:#f3f4f6; }
  .dc-modal-title { margin:0 0 8px; font-size:20px; font-weight:900; color:#111827; }
  .dc-meta { font-size:13px; color:#000; display:grid; gap:6px; margin-bottom:12px; }
  .dc-cta { border:1px solid #111827; background:#111827; color:#fff; border-radius:10px; padding:10px 14px; font-size:13px; font-weight:800; cursor:pointer; }

  @media(max-width:1100px){ .dc-grid{grid-template-columns:repeat(2,minmax(0,1fr));} .dc-skeleton{grid-template-columns:repeat(2,minmax(0,1fr));} }
  @media(max-width:900px){
    .dc-layout{grid-template-columns:1fr;}
    .dc-mobile-toggle{display:block;}
    .dc-filter-card{display:none;}
    .dc-filter-card.show{display:block;}
  }
  @media(max-width:700px){ .dc-grid{grid-template-columns:1fr;} .dc-skeleton{grid-template-columns:1fr;} .dc-modal-body{grid-template-columns:1fr;} }
`;

function DesignModal({ design, onClose, onCreateQuotation }) {
  if (!design) return null;
  return (
    <div className="dc-modal-overlay" onClick={onClose}>
      <div className="dc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dc-modal-head">
          <strong style={{ color: '#111827' }}>Design Detail</strong>
          <button className="dc-btn" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="dc-modal-body">
          <img className="dc-modal-img" src={design.thumbnail_url || ''} alt={design.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <div>
            <h3 className="dc-modal-title">{design.name}</h3>
            <div className="dc-chip"><Tag size={11} />{design.category || 'Uncategorized'}</div>
            <div className="dc-meta">
              <div><strong>Design Code:</strong> {design.design_code || '-'}</div>
              <div><strong>Base Price:</strong> Rs {Number(design.base_price || 0).toLocaleString('en-IN')}</div>
              <div><strong>Estimated Delivery:</strong> {design.estimated_days || 0} days</div>
              <div><strong>Weight:</strong> {design.piece_weight_grams ?? '-'} g</div>
              <div><strong>Complexity:</strong> {design.complexity_weight ?? '-'}</div>
            </div>
            <div style={{ fontSize: 13, color: '#1a1a1a', marginBottom: 14 }}>{design.description || 'No description available.'}</div>
            <button className="dc-cta" onClick={() => onCreateQuotation(design)}>Create Quotation</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DesignCatalogue({ onSelectDesign }) {
  const { getValues } = useMasterData('DESIGN_CATEGORY');
  const defaultCategories = getValues('DESIGN_CATEGORY');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minDays, setMinDays] = useState('');
  const [maxDays, setMaxDays] = useState('');
  const [categories, setCategories] = useState(defaultCategories);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [modalDesign, setModalDesign] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (minPrice) params.min_price = Number(minPrice);
      if (maxPrice) params.max_price = Number(maxPrice);
      if (minDays) params.min_days = Number(minDays);
      if (maxDays) params.max_days = Number(maxDays);

      const allRows = [];
      if (selectedCategories.length === 0) {
        const res = await api.get('/sales/design-catalogue', { params });
        allRows.push(...(res?.data?.data || []));
      } else {
        const reqs = selectedCategories.map((cat) => api.get('/sales/design-catalogue', { params: { ...params, category: cat } }));
        const responses = await Promise.all(reqs);
        responses.forEach((res) => allRows.push(...(res?.data?.data || [])));
      }

      const dedup = Array.from(new Map(allRows.map((row) => [row.id, row])).values());
      setRows(dedup);

      const catSet = new Set(defaultCategories);
      dedup.forEach((row) => { if (row.category) catSet.add(row.category); });
      setCategories(Array.from(catSet));
    } catch (e) {
      setRows([]);
      setError(e?.response?.data?.message || 'Failed to load design catalogue.');
    } finally {
      setLoading(false);
    }
  }, [defaultCategories, maxDays, maxPrice, minDays, minPrice, search, selectedCategories]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    if (defaultCategories.length) setCategories((prev) => (prev.length ? prev : defaultCategories));
  }, [defaultCategories]);

  const filteredRows = useMemo(() => rows, [rows]);

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) => prev.includes(cat) ? prev.filter((x) => x !== cat) : [...prev, cat]);
  };

  const createQuotation = (design) => {
    const url = new URL(window.location.href);
    url.searchParams.set('design_id', String(design.id));
    window.history.replaceState({}, '', url.toString());
    onSelectDesign?.({
      id: design.id,
      design_id: design.id,
      design_code: design.design_code,
      design_name: design.name,
      image_url: design.thumbnail_url,
      actual_weight: design.piece_weight_grams,
      category: design.category,
      description: design.description,
      complexity_weight: design.complexity_weight,
    });
  };

  const filterPanel = (
    <div className={`dc-filter-card ${mobileFiltersOpen ? 'show' : ''}`}>
      <div className="dc-filter-title"><Filter size={14} /> Filters</div>

      <label className="dc-label">Search</label>
      <input className="dc-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Design name / code" />

      <div style={{ height: 10 }} />
      <label className="dc-label">Categories</label>
      <div className="dc-cat-list">
        {categories.map((cat) => (
          <label className="dc-cat-item" key={cat}>
            <input type="checkbox" checked={selectedCategories.includes(cat)} onChange={() => toggleCategory(cat)} />
            <span>{cat}</span>
          </label>
        ))}
      </div>

      <div style={{ height: 10 }} />
      <label className="dc-label">Price Range (Rs)</label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <input className="dc-input" type="number" placeholder="Min" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
        <input className="dc-input" type="number" placeholder="Max" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
      </div>

      <div style={{ height: 10 }} />
      <label className="dc-label">Delivery Days</label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <input className="dc-input" type="number" placeholder="Min" value={minDays} onChange={(e) => setMinDays(e.target.value)} />
        <input className="dc-input" type="number" placeholder="Max" value={maxDays} onChange={(e) => setMaxDays(e.target.value)} />
      </div>
    </div>
  );

  return (
    <>
      <style>{S}</style>
      <div className="dc-wrap">
        <div className="dc-top">
          <div>
            <h1 className="dc-title">Design Catalogue</h1>
            <div className="dc-sub">Browse active designs and create quotations quickly.</div>
          </div>
          <button className="dc-btn" onClick={load}>Refresh</button>
        </div>

        <button className="dc-btn dc-mobile-toggle" onClick={() => setMobileFiltersOpen((v) => !v)}>
          <Filter size={14} style={{ marginRight: 6 }} /> {mobileFiltersOpen ? 'Hide Filters' : 'Show Filters'}
        </button>

        <div className="dc-layout">
          {filterPanel}

          <div>
            {loading ? (
              <div className="dc-skeleton">{Array.from({ length: 9 }).map((_, i) => <div key={i} className="dc-sk" />)}</div>
            ) : error ? (
              <div className="dc-empty" style={{ color: '#b91c1c' }}>{error}</div>
            ) : filteredRows.length === 0 ? (
              <div className="dc-empty">No designs found for selected filters.</div>
            ) : (
              <div className="dc-grid">
                {filteredRows.map((row) => (
                  <div key={row.id} className="dc-card" onClick={() => setModalDesign(row)}>
                    {row.thumbnail_url ? (
                      <img className="dc-img" src={row.thumbnail_url} alt={row.name} />
                    ) : (
                      <div className="dc-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                        <Tag size={34} />
                      </div>
                    )}
                    <div className="dc-body">
                      <div className="dc-name">{row.name}</div>
                      <div className="dc-chip"><Tag size={11} />{row.category || 'Uncategorized'}</div>
                      <div className="dc-row"><span className="dc-price">Rs {Number(row.base_price || 0).toLocaleString('en-IN')}</span></div>
                      <div className="dc-row"><span><Truck size={12} style={{ marginRight: 4 }} /> {row.estimated_days || 0} days</span><span><Weight size={12} style={{ marginRight: 4 }} /> {row.piece_weight_grams ?? '-'}g</span></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <DesignModal design={modalDesign} onClose={() => setModalDesign(null)} onCreateQuotation={createQuotation} />
    </>
  );
}
